import { Button } from "@/components/ui/UIElements/Button";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { Breadcrumb, Icon, Input, Label, NativeSelect } from "../../ui";
import { DeleteConfirmationModal } from "../../ui/Modals";
import { Switch } from "../../ui/UIElements/Switch";
import { useMetaAdsCampaignForEdit } from "../../../hooks/metaAds/useMetaAdsCampaignForEdit";
import { useMetaAdsDeleteCampaign } from "../../../hooks/metaAds/useMetaAdsDeleteCampaign";
import {
  isMetaAdsApiConfigured,
  patchMetaAdsCampaignSettings,
} from "../../../hooks/metaAds/useMetaAdsApi";
import { isMetaAdsExperienceAvailable } from "../../../hooks/metaAds/useMetaAdsMode";
import { cn } from "@/lib/utils";
import {
  metaAdsDangerZoneDeleteButtonClass,
  metaAdsFormFieldLabelClass,
  metaAdsFormSectionClass,
  metaAdsGradientCtaClass,
  metaAdsOutlineCtaClass,
  metaAdsSelectClass,
  metaAdsTitleGradientClass,
} from "./metaAdsTheme";
import {
  formatMetaAdsCampaignObjective,
  formatMetaAdsMoneyFromCents,
} from "./metaAdsUtils";

const META_CAMPAIGN_SPEND_CAP_MIN_CENTS = 10000;

function formatMetaAdsApiLabel(value) {
  const raw = value != null ? String(value).trim() : "";
  if (!raw) {
    return "—";
  }
  return raw
    .replace(/^OUTCOME_/i, "")
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
}

const MetaAdsEditCampaignPage = () => {
  const { campaignId } = useParams();
  const { t } = useTranslation("metaAds");
  const { t: tNav } = useTranslation("navigation");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const apiConfigured = isMetaAdsApiConfigured();
  const unlocked = isMetaAdsExperienceAvailable(apiConfigured);
  const normalizedCampaignId = String(campaignId ?? "").trim();

  const {
    data: campaignDetail,
    isLoading,
    isError,
    error,
  } = useMetaAdsCampaignForEdit(normalizedCampaignId, {
    enabled: Boolean(unlocked && normalizedCampaignId),
  });

  const [name, setName] = useState("");
  const [isAdsetBudgetSharingEnabled, setIsAdsetBudgetSharingEnabled] =
    useState(false);
  const [hasCampaignSpendLimit, setHasCampaignSpendLimit] = useState(false);
  const [campaignSpendLimitDollars, setCampaignSpendLimitDollars] =
    useState("");
  const [spendLimitConfirmOpen, setSpendLimitConfirmOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const hydratedKeyRef = useRef(null);

  useEffect(() => {
    if (!campaignDetail?.campaignId) {
      return;
    }
    const key = String(campaignDetail.campaignId);
    if (hydratedKeyRef.current === key) {
      return;
    }
    hydratedKeyRef.current = key;
    setName(campaignDetail.name || "");
    setIsAdsetBudgetSharingEnabled(
      Boolean(campaignDetail.isAdsetBudgetSharingEnabled),
    );
    const spendCapCents = Number(campaignDetail.campaignSpendCapCents ?? 0);
    setHasCampaignSpendLimit(
      Number.isFinite(spendCapCents) && spendCapCents > 0,
    );
    setCampaignSpendLimitDollars(
      Number.isFinite(spendCapCents) && spendCapCents > 0
        ? (spendCapCents / 100).toFixed(2)
        : "",
    );
  }, [campaignDetail]);

  const breadcrumbItems = useMemo(
    () => [
      { label: tNav("sidebar.growth"), disabled: true },
      {
        label: tNav("sidebar.metaAds"),
        path: "/growth/meta-ads",
        icon: "Share2",
      },
      {
        label: t("create.editBreadcrumbLabel"),
        active: true,
        disabled: true,
      },
    ],
    [t, tNav],
  );

  const objectiveLabel = formatMetaAdsCampaignObjective(
    campaignDetail?.objective,
    t,
  );
  const buyingTypeLabel = formatMetaAdsApiLabel(campaignDetail?.buyingType);
  const campaignLifetimeSpendCents = Number(
    campaignDetail?.campaignLifetimeSpendCents ?? 0,
  );
  const campaignSpendLimitPreviewCents = (() => {
    const raw = String(campaignSpendLimitDollars).trim();
    if (!raw) {
      return null;
    }
    const dollars = Number.parseFloat(raw.replace(",", "."));
    if (!Number.isFinite(dollars)) {
      return null;
    }
    return Math.round(dollars * 100);
  })();
  const effectiveCampaignSpendCapCents =
    hasCampaignSpendLimit && campaignSpendLimitPreviewCents != null
      ? campaignSpendLimitPreviewCents
      : 0;
  const apiCampaignSpendCapCents = Number(
    campaignDetail?.campaignSpendCapCents ?? 0,
  );
  const hasCampaignSettingsChanges = Boolean(
    campaignDetail &&
    (name.trim() !== String(campaignDetail.name ?? "").trim() ||
      isAdsetBudgetSharingEnabled !==
        Boolean(campaignDetail.isAdsetBudgetSharingEnabled) ||
      effectiveCampaignSpendCapCents !==
        (Number.isFinite(apiCampaignSpendCapCents)
          ? apiCampaignSpendCapCents
          : 0)),
  );
  const showCampaignSpendLimitMinimumHint =
    hasCampaignSpendLimit &&
    campaignSpendLimitPreviewCents != null &&
    campaignSpendLimitPreviewCents > 0 &&
    campaignSpendLimitPreviewCents < META_CAMPAIGN_SPEND_CAP_MIN_CENTS;
  const hasValidCampaignSpendLimit =
    campaignSpendLimitPreviewCents != null &&
    campaignSpendLimitPreviewCents >= META_CAMPAIGN_SPEND_CAP_MIN_CENTS &&
    (!Number.isFinite(campaignLifetimeSpendCents) ||
      campaignLifetimeSpendCents <= 0 ||
      campaignSpendLimitPreviewCents > campaignLifetimeSpendCents);

  const saveMutation = useMutation({
    mutationFn: (body) =>
      patchMetaAdsCampaignSettings(normalizedCampaignId, body),
    onSuccess: (data) => {
      showToast(t("create.editSuccessToast"), "success");
      queryClient.setQueryData(
        ["meta-ads-campaign-detail", normalizedCampaignId],
        data,
      );
      queryClient.invalidateQueries({
        queryKey: ["meta-ads-graph-campaigns"],
        exact: false,
      });
    },
    onError: (err) => {
      showToast(err?.message || t("create.editSaveErrorToast"), "error");
    },
  });

  const deleteCampaignMutation = useMetaAdsDeleteCampaign();

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!normalizedCampaignId || !name.trim()) {
      showToast(t("create.campaignNameRequired"), "error");
      return;
    }
    let campaignSpendCapCents = 0;
    if (hasCampaignSpendLimit) {
      const rawSpendLimit = String(campaignSpendLimitDollars).trim();
      if (rawSpendLimit) {
        const dollars = Number.parseFloat(rawSpendLimit.replace(",", "."));
        if (!Number.isFinite(dollars) || dollars < 0) {
          showToast(t("create.campaignSpendLimitRequired"), "error");
          return;
        }
        campaignSpendCapCents = Math.round(dollars * 100);
        if (
          campaignSpendCapCents > 0 &&
          campaignSpendCapCents < META_CAMPAIGN_SPEND_CAP_MIN_CENTS
        ) {
          showToast(t("create.campaignSpendLimitMinimum"), "error");
          return;
        }
        if (
          campaignSpendCapCents > 0 &&
          Number.isFinite(campaignLifetimeSpendCents) &&
          campaignLifetimeSpendCents > 0 &&
          campaignSpendCapCents <= campaignLifetimeSpendCents
        ) {
          showToast(t("create.campaignSpendLimitTooLow"), "error");
          return;
        }
      }
    }
    saveMutation.mutate({
      name: name.trim(),
      isAdsetBudgetSharingEnabled,
      campaignSpendCapCents,
    });
  };

  const handleConfirmDeleteCampaign = () => {
    if (!normalizedCampaignId) {
      setDeleteModalOpen(false);
      return;
    }
    deleteCampaignMutation.mutate(
      { campaignId: normalizedCampaignId },
      {
        onSuccess: () => {
          setDeleteModalOpen(false);
          navigate("/growth/meta-ads/performance");
        },
      },
    );
  };

  const isSaving = saveMutation.isPending;
  const disableSubmit =
    !unlocked ||
    isLoading ||
    isError ||
    isSaving ||
    !name.trim() ||
    !hasCampaignSettingsChanges;

  return (
    <div className="space-y-10">
      <Breadcrumb items={breadcrumbItems} />

      {!unlocked && (
        <div className="rounded-[var(--od-radius-panel)] border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          {t("hub.apiNotConfigured")}
        </div>
      )}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            <span className={metaAdsTitleGradientClass}>
              {t("create.editCampaignSettingsTitleGradientWord")}
            </span>
            <span className="text-foreground">
              {t("create.editCampaignSettingsTitleRest")}
            </span>
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl text-sm sm:text-base">
            {t("create.editCampaignSettingsSubtitle")}
          </p>
        </div>
        <Link
          to="/growth/meta-ads/performance"
          className={cn(
            metaAdsOutlineCtaClass,
            "focus-visible:ring-ring inline-flex w-full shrink-0 items-center justify-center px-4 py-2.5 focus-visible:ring-2 focus-visible:outline-none sm:w-auto",
          )}
        >
          {t("create.cancelToPerformance")}
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((item) => (
            <section key={item} className={metaAdsFormSectionClass}>
              <div className="bg-muted/30 h-24 animate-pulse rounded-[var(--od-radius-panel)]" />
            </section>
          ))}
        </div>
      ) : isError ? (
        <div className="border-destructive/35 bg-destructive/10 rounded-[var(--od-radius-panel)] border px-4 py-6 text-sm text-red-100">
          {error?.message || t("create.editError")}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          <section className={metaAdsFormSectionClass}>
            <div className="mb-5 flex items-center gap-2">
              <Icon
                name="CheckCircle2"
                className="h-5 w-5 text-emerald-400"
                aria-hidden
              />
              <h2 className="text-foreground text-lg font-semibold">
                {t("create.campaignDetailsSection")}
              </h2>
            </div>
            <div className="space-y-5">
              <div className="space-y-2">
                <Label
                  htmlFor="meta-campaign-name"
                  className={metaAdsFormFieldLabelClass}
                >
                  {t("create.campaignName")}
                </Label>
                <Input
                  id="meta-campaign-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder={t("create.campaignNamePlaceholder")}
                  required
                  variant="dark"
                  size="lg"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label
                    htmlFor="meta-buying-type"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.campaignBuyingType")}
                  </Label>
                  <NativeSelect
                    id="meta-buying-type"
                    className={cn(metaAdsSelectClass, "opacity-70")}
                    value={buyingTypeLabel}
                    disabled
                  >
                    <option value={buyingTypeLabel}>{buyingTypeLabel}</option>
                  </NativeSelect>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="meta-campaign-objective"
                    className={metaAdsFormFieldLabelClass}
                  >
                    {t("create.campaignGoalReadonly")}
                  </Label>
                  <NativeSelect
                    id="meta-campaign-objective"
                    className={cn(metaAdsSelectClass, "opacity-70")}
                    value={objectiveLabel}
                    disabled
                  >
                    <option value={objectiveLabel}>{objectiveLabel}</option>
                  </NativeSelect>
                </div>
              </div>
            </div>
          </section>

          <section className={metaAdsFormSectionClass}>
            <div className="mb-5 flex items-center gap-2">
              <Icon
                name="CheckCircle2"
                className="h-5 w-5 text-emerald-400"
                aria-hidden
              />
              <h2 className="text-foreground text-lg font-semibold">
                {t("create.campaignBudgetSection")}
              </h2>
            </div>
            <div className="rounded-[var(--od-radius-panel)] bg-sky-500/10 p-2">
              <div className="mb-2 flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-foreground text-sm font-semibold">
                    {t("create.campaignBudgetStrategy")}
                  </h3>
                </div>
                <Icon
                  name="ChevronUp"
                  className="text-muted-foreground h-4 w-4"
                  aria-hidden
                />
              </div>
              <div className="bg-background/80 rounded-lg p-4">
                <div className="space-y-4">
                  <div className="flex items-start gap-3 opacity-60">
                    <span className="border-muted-foreground/50 mt-1 h-6 w-6 rounded-full border" />
                    <div>
                      <p className="text-foreground text-sm font-medium">
                        {t("create.campaignBudgetCampaignOption")}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {t("create.campaignBudgetCampaignOptionHint")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="border-primary mt-1 flex h-6 w-6 items-center justify-center rounded-full border">
                      <span className="bg-primary h-3 w-3 rounded-full" />
                    </span>
                    <div>
                      <p className="text-foreground text-sm font-medium">
                        {t("create.campaignBudgetAdSetOption")}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {t("create.campaignBudgetAdSetOptionHint")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3">
              <Switch
                id="meta-budget-sharing"
                checked={isAdsetBudgetSharingEnabled}
                onCheckedChange={setIsAdsetBudgetSharingEnabled}
              />
              <Label
                htmlFor="meta-budget-sharing"
                className="text-foreground text-sm font-medium"
              >
                {t("create.budgetSharingLabel")}
              </Label>
            </div>

            <div
              className="border-border/60 bg-muted/10 mt-6 space-y-4 rounded-[var(--od-radius-panel)] border p-4 sm:p-5"
              aria-labelledby="meta-campaign-spend-limit-heading"
            >
              <div className="flex flex-wrap items-center gap-2 gap-y-1">
                <p
                  id="meta-campaign-spend-limit-heading"
                  className="text-foreground text-sm font-semibold"
                >
                  {t("create.campaignSpendLimitLabel")}
                </p>
                <span className="bg-muted/60 text-muted-foreground rounded-md px-2 py-0.5 text-xs font-medium">
                  {t("create.campaignSpendLimitOptional")}
                </span>
              </div>
              <div className="border-border/60 bg-background/60 flex items-start gap-3 rounded-lg border p-3">
                <Icon
                  name="Info"
                  className="text-primary mt-0.5 h-4 w-4 flex-shrink-0"
                  aria-hidden
                />
                <div className="min-w-0 space-y-1">
                  <p className="text-foreground text-sm font-medium">
                    {t("create.campaignTotalSpend", {
                      amount: formatMetaAdsMoneyFromCents(
                        Number.isFinite(campaignLifetimeSpendCents)
                          ? campaignLifetimeSpendCents
                          : 0,
                      ),
                    })}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    {t("create.campaignSpendLimitTotalSpendHint")}
                  </p>
                </div>
              </div>
              <button
                type="button"
                id="meta-campaign-spend-limit-toggle"
                aria-pressed={hasCampaignSpendLimit}
                onClick={() => {
                  const next = !hasCampaignSpendLimit;
                  if (!next) {
                    if (hasValidCampaignSpendLimit) {
                      setSpendLimitConfirmOpen(true);
                      return;
                    }
                    setCampaignSpendLimitDollars("");
                    setHasCampaignSpendLimit(false);
                    return;
                  }
                  setHasCampaignSpendLimit(next);
                }}
                className={cn(
                  "focus-visible:ring-ring flex w-full cursor-pointer items-center gap-3 rounded-lg p-3 text-left text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none",
                  hasCampaignSpendLimit
                    ? "bg-blue-600/10 hover:bg-blue-600/20"
                    : "hover:bg-muted/40",
                )}
              >
                <div
                  className={cn(
                    "flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border transition-colors",
                    hasCampaignSpendLimit
                      ? "border-blue-500 bg-blue-500"
                      : "border-gray-600 bg-gray-950",
                  )}
                >
                  {hasCampaignSpendLimit ? (
                    <Icon
                      name="Check"
                      className="h-3 w-3 text-white"
                      aria-hidden
                    />
                  ) : null}
                </div>
                <span className="text-foreground font-medium">
                  {t("create.campaignSpendLimitToggle")}
                </span>
              </button>
              <div className="relative">
                <span
                  className="pointer-events-none absolute top-1/2 left-3 z-10 -translate-y-1/2 text-sm text-gray-400"
                  aria-hidden
                >
                  $
                </span>
                <Input
                  id="meta-campaign-spend-limit"
                  inputMode="decimal"
                  value={campaignSpendLimitDollars}
                  placeholder={t("create.campaignSpendLimitPlaceholder")}
                  onChange={(event) =>
                    setCampaignSpendLimitDollars(event.target.value)
                  }
                  disabled={!hasCampaignSpendLimit}
                  variant="dark"
                  size="lg"
                  className={cn(
                    "pl-7",
                    showCampaignSpendLimitMinimumHint &&
                      "border-red-500 focus:border-red-500 focus:ring-red-500/50",
                  )}
                  aria-describedby={
                    showCampaignSpendLimitMinimumHint
                      ? "meta-campaign-spend-limit-error"
                      : undefined
                  }
                />
              </div>
              {showCampaignSpendLimitMinimumHint && (
                <p
                  id="meta-campaign-spend-limit-error"
                  className="text-xs font-medium text-red-400"
                >
                  {t("create.campaignSpendLimitMinimumHint", {
                    amount: formatMetaAdsMoneyFromCents(
                      META_CAMPAIGN_SPEND_CAP_MIN_CENTS,
                    ),
                  })}
                </p>
              )}
            </div>
          </section>

          <DeleteConfirmationModal
            confirmButtonText={t(
              "create.campaignSpendLimitRemoveConfirmButton",
            )}
            detailNote={t("create.campaignSpendLimitRemoveModalDetail")}
            isLoading={false}
            isOpen={spendLimitConfirmOpen}
            itemName={t("create.campaignSpendLimitLabel")}
            itemType={t("create.campaignSpendLimitLabel")}
            onClose={() => setSpendLimitConfirmOpen(false)}
            onConfirm={() => {
              setHasCampaignSpendLimit(false);
              setCampaignSpendLimitDollars("");
              setSpendLimitConfirmOpen(false);
            }}
            subtitle={t("create.campaignSpendLimitRemoveModalSubtitle")}
            title={t("create.campaignSpendLimitRemoveConfirm")}
          />

          <button
            type="submit"
            disabled={disableSubmit}
            className={cn(
              "flex w-full items-center justify-center gap-2 py-3.5 transition disabled:cursor-not-allowed disabled:opacity-50",
              metaAdsGradientCtaClass,
            )}
          >
            {isSaving ? (
              t("create.editSubmitting")
            ) : (
              <>
                <Icon name="Save" className="h-5 w-5" aria-hidden />
                {t("create.editSubmitLabel")}
              </>
            )}
          </button>

          <DeleteConfirmationModal
            confirmLoadingText={t("create.deleteCampaignDeleting")}
            detailNote={t("create.deleteCampaignIrreversible")}
            isLoading={deleteCampaignMutation.isPending}
            isOpen={deleteModalOpen}
            itemName={name.trim() || normalizedCampaignId}
            itemType={t("create.deleteCampaignItemType")}
            onClose={() => setDeleteModalOpen(false)}
            onConfirm={handleConfirmDeleteCampaign}
            subtitle={t("create.deleteCampaignModalSubtitle")}
            title={t("create.deleteCampaignModalTitle")}
            confirmButtonText={t("create.deleteCampaignConfirm")}
          />
          <div className="border-border/70 mt-14 border-t pt-10">
            <h2 className="text-foreground mb-5 text-xl font-semibold tracking-tight">
              {t("create.deleteCampaignSectionLabel")}
            </h2>
            <div className="bg-muted/25 border-destructive/50 rounded-[var(--od-radius-panel)] border shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
              <div className="flex flex-col gap-6 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-8 sm:px-6 sm:py-6">
                <div className="min-w-0 space-y-2 sm:max-w-2xl sm:flex-1">
                  <h3 className="text-foreground text-base leading-snug font-semibold sm:text-[17px]">
                    {t("create.deleteCampaignDangerHeading")}
                  </h3>
                  <p className="text-muted-foreground max-w-2xl text-sm leading-relaxed">
                    {t("create.deleteCampaignSectionHint")}
                  </p>
                </div>
                <Button
                  emphasis="outline"
                  intent="danger"
                  type="button"
                  disabled={
                    !unlocked ||
                    isLoading ||
                    Boolean(isError) ||
                    deleteCampaignMutation.isPending ||
                    isSaving
                  }
                  onClick={() => setDeleteModalOpen(true)}
                  className={cn(
                    metaAdsDangerZoneDeleteButtonClass,
                    "sm:w-auto",
                  )}
                >
                  {deleteCampaignMutation.isPending ? (
                    <Icon
                      name="Loader2"
                      className="h-4 w-4 animate-spin"
                      aria-hidden
                    />
                  ) : (
                    <Icon name="Trash2" className="h-4 w-4" aria-hidden />
                  )}
                  {deleteCampaignMutation.isPending
                    ? t("create.deleteCampaignDeleting")
                    : t("create.deleteCampaign")}
                </Button>
              </div>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default MetaAdsEditCampaignPage;
